import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { SavedAndSubscribedFactory } from '@ddt-web/blk-model-subscription-manager';
import { APPLICATION_NAME_TOKEN, ApplicationName } from '@ddt-web/config';
import { PortfolioTaggingService } from '@ddt-web/data-service';
import { PortfolioWithWdsTagging, UserDataApi, WdsTaggingAttributes, WdsTaggingConfig, WebUserDataAppConfig, WebUserDataConstants } from '@ddt-web/domain';
import { UserDataService } from '@ddt-web/user-data';
import * as chunk from 'lodash/chunk';
import * as get from 'lodash/get';
import { Observable, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class WebToolsPlatformDataService implements UserDataApi {

	private getAllJson$: Subject<any> = new Subject<any>();

	public getAllJson(): Observable<any> {
		return this.getAllJson$.asObservable();
	}

	constructor(
		private http: HttpClient,
		private servicePathProvider: ServicePathProviderService,
		private savedAndSubscribedFactory: SavedAndSubscribedFactory,
		private portfolioTaggingService: PortfolioTaggingService,
		private userDataService: UserDataService,
		@Inject(APPLICATION_NAME_TOKEN) private applicationName: ApplicationName
	) { }

	public getModelPortfoliosConfigFromAPI(): Observable<any> {
		const url: string = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.MODEL_PORTFOLIOS_CONFIG;
		return this.http.get(url, {
			params: { viewId: 'modelPortfolio@blackrock' }
		});
	}

	public getManagedModelsConfigFromAPI(): Observable<any> {
		const url: string = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.MANAGED_MODELS_CONFIG;
		return this.http.get(url);
	}

	public getCustomizedPortfoliosByIdFromAPI(id: string | Array<string>): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.GET;
		return this.http.post(url, { uniqueIds: id });
	}

	public loadSavedAndSubscribedModelIds(): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.GET_ALL;
		return this.http
			.get(url, {
				params: { rc: 'bfa:get-saved-subscribed-prefs' }
			})
			.pipe(
				map((response: Response) => {
					this.cleanupSaveAndSubscribeDeltas(response);
					return response;
				}),
				map((response: Response) => {
					this.getAllJson$.next(response);
					return response;
				})
			);
	}

	private cleanupSaveAndSubscribeDeltas(response: Response): void {
		const deltaUniqueIds: string[] = this.savedAndSubscribedFactory.getDeltaIds(response);
		const savedMaster: any = this.savedAndSubscribedFactory.getSavedMaster(response);
		const subscribedMaster: any = this.savedAndSubscribedFactory.getSubscribedMaster(response);
		this.updateMasterListPreference(savedMaster, subscribedMaster, deltaUniqueIds);
	}

	public updateMasterListPreference(savedMaster: any, subscribedMaster: any, deltaIds: string[]): void {
		if (deltaIds.length) {
			// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
			this.updatePrefs(savedMaster, subscribedMaster).subscribe(
				response => {
					chunk(deltaIds, 100).map(bulkIds => {
						// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
						this.removeUserDataObjectsFromAPI(bulkIds).subscribe(
							result => {
								return result;
							},
							error => {
								console.log(`${error} - Background removal still running in WTP`);
							}
						);
					});
				},
				error => {
					console.log(`Error occurred updating master list prefs: ${error}`);
				}
			);
		}
	}

	private updatePrefs(savedMaster: any, subscribedMaster: any): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.EDIT;
		return this.http.post(url, {
			allowCreateIfMissing: 'true',
			rcs: 'bfa-new-data',
			datas: '[' + this.createEditJsonForAPI(savedMaster) + ',' + this.createEditJsonForAPI(subscribedMaster) + ']'
		});
	}

	public removeUserDataObjectsFromAPI(uniqueIds: Array<string>): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.REMOVE;
		return this.http.post(url, { uniqueIds: uniqueIds.toString() });
	}

	public createEditJsonForAPI(masterList: {}): string {
		const dataObject = {
			preferenceKey: masterList['preferenceKey'],
			type: 'PORTFOLIO_LIKE',
			children: []
		};
		masterList['children'].forEach(child => {
			const childObject = {
				preferenceKey: child['preferenceKey'],
				identifier: child['identifier'],
				identifierType: 'PORTFOLIO_ID',
				type: 'PORTFOLIO_POSITION_LIKE'
			};
			dataObject['children'].push(childObject);
		});

		this.addTaggingToWdsSavedObjects(dataObject);

		return JSON.stringify(dataObject);
	}

	/**
	 * Searches if there are any portfolios with the provided portfolioName.
	 * If the resultList that is returned is empty, there is no portfolio with the provided name.
	 *
	 * @param portfolioName
	 */
	public searchPortfolios(portfolioName: string): Observable<any> {
		return this.userDataService.isAladdinuUser$().pipe(
			switchMap(isAladdinuUser => {
				const wudApplicationTag = WebUserDataAppConfig.APPLICATION_NAMES_PORTFOLIO_SAVING[this.applicationName].defaultApplication;
				let applicationFilter = '';

				// Catering for aladdinU edge case - still a 360 app but has a different applications filter
				if (isAladdinuUser) {
					applicationFilter = `${WebUserDataConstants.APPLICATIONS.BFA_ALADDINU}`;
				} else {
					applicationFilter = `${wudApplicationTag}`;
				}
				return this.http
					.post(this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.SEARCH, {
						useCache: 'false',
						ignoreDataFromOthers: 'true',
						filter: `description:"${portfolioName}" AND applications:"${applicationFilter}"`
					})
					.pipe(
						map((response: any) => get(response, 'resultMap.SEARCH_RESULTS[0].resultList'))
					);
			})
		);
	}

	// This method should replace the above one : saveModelPortfolioToAPI
	public savePortfolioToAPI(portfolio: any): Observable<any> {
		const params: any = {};
		if (portfolio.uniqueId && portfolio.subType !== 'PARTNER_MODEL') {
			params.allowOverwrite = 'true';
		} else {
			params.rc = 'bfa-new-data';
		}

		const holdingPropertiesForSave = ['_identifier', '_identifierType', '_subType', '_weight'];
		const dataObject = {};
		Object.getOwnPropertyNames(portfolio).forEach(property => {
			if (property === '_children') {
				dataObject[property.replace('_', '')] = portfolio[property].map(child => {
					const childObject = {};
					Object.getOwnPropertyNames(child).forEach(childProperty => {
						if (holdingPropertiesForSave.indexOf(childProperty) > -1) {
							childObject[childProperty.replace('_', '')] = child[childProperty];
						}
					});
					return childObject;
				});
			} else {
				dataObject[property.replace('_', '')] = portfolio[property];
			}
		});

		this.addTaggingToWdsSavedObjects(dataObject);

		params.data = JSON.stringify(dataObject);
		params.includeAllowedPermsForCurrentUser = 'true';
		const url =
			this.servicePathProvider.getServicePath('api-gateway') +
			(portfolio.uniqueId ? WebUserDataConstants.USER_DATA_URL.EDIT : WebUserDataConstants.USER_DATA_URL.ADD_PORTFOLIO);
		return this.http.post(url, params);
	}

	public editObject(objData: {}, additionalParams = {}): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.EDIT;
		const params = { datas: JSON.stringify(objData), allowOverwrite: 'true', ...additionalParams };
		return this.http.post(url, params);
	}

	private addTaggingToWdsSavedObjects(dataObject: PortfolioWithWdsTagging): void {
		Object.assign(
			dataObject,
			this.portfolioTaggingService.setAnalyticsTags(
				dataObject,
				{
					isNewRecord: true,
					overrides: {
						toolOrigin: WebUserDataConstants.APPLICATIONS.BFA_MODELS,
						origin: WebUserDataConstants.APPLICATIONS.BFA_MODELS
					} as WdsTaggingAttributes

				} as WdsTaggingConfig
			)
		);
	}
}
